import useSearchMembers from '@/contexts/chat/scripts/hooks/useSearchMembers';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import styles from './index.module.scss';
import SearchCommon from './search-common';

type Member = ChatGroupMember;

export interface SearchMembersList {
  contacts: Member[];
  members: Member[];
  users: Member[];
}
interface SearchMembersProps {
  className?: ClassNames;
  searchBodyClassName?: ClassNames;
  excludeMembers?: Member[];
  membersList?: SearchMembersList;
  updateMembersList?: (list: SearchMembersList) => void;
  selectedList?: Member[];
  onSelect: (item: Member[]) => void;
}

export default function SearchMembers({
  className,
  searchBodyClassName = '',
  excludeMembers = [],
  membersList = {
    contacts: [],
    members: [],
    users: [],
  },
  updateMembersList,
  selectedList,
  onSelect,
}: SearchMembersProps) {
  const [value, setValue] = useState('');

  const { list: contacts } = useSearchMembers<Member>({
    value,
    type: 'contacts',
    excludeMembers,
  });
  const { list: members } = useSearchMembers<Member>({
    value,
    type: 'chat',
    excludeMembers,
  });
  const { list: users } = useSearchMembers<Member>({
    value,
    type: 'global',
    excludeMembers,
  });

  const filterMembers = useMemo(() => {
    const ids = new Set(contacts.map((item) => item.user_id));
    return members.filter((item) => !ids.has(item.user_id));
  }, [members, contacts]);

  const filterUsers = useMemo(() => {
    const ids = new Set([...contacts, ...filterMembers].map((item) => item.user_id));
    return users.filter((item) => !ids.has(item.user_id));
  }, [contacts, filterMembers, users]);

  useLayoutEffect(() => {
    updateMembersList?.({
      contacts,
      members: filterMembers,
      users: filterUsers,
    });
  }, [contacts, filterMembers, filterUsers, members, updateMembersList, users]);

  return (
    <div className={cls(styles.searchMembers, className)}>
      <div className={styles.search}>
        <Input
          value={value}
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          variant="filled"
          placeholder="Search"
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <div className={cls(styles.searchBody, searchBodyClassName, 'scrollbar_hover')}>
        <SearchCommon<Member>
          list={membersList.contacts}
          title="Members in contacts"
          selectedList={selectedList}
          onSelect={onSelect}
        />
        <SearchCommon<Member>
          list={membersList.members}
          title="Members in chats"
          selectedList={selectedList}
          onSelect={onSelect}
        />
        <SearchCommon<Member>
          list={membersList.users}
          title="Members in global"
          selectedList={selectedList}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
}
